import { z } from "zod";
import { ORIGINAL_KEYWORD } from "./catalog-taxonomy.js";
import { ENGINE_TARGETS, RuntimeProviderSchema } from "./runtime.js";
import { SiteIdSchema } from "./sites.js";
import { CatalogTemplateKindSchema, CONTENT_API_VERSION, ContentKeySchema, } from "./wire.js";
/**
 * The shelf rules: what a front-end lists from the catalog, how a filter, a search and a
 * count work over it, and in what order it shows (V6 plan §13.3.3 "One shelf module for every
 * client and the server", row H.6). Pure: no React, no URL, no I/O. The SPA's gallery calls
 * it today; the server's `catalog.list` and any TypeScript front-end call the same functions,
 * so every client lists the same shelf. The SPA's URL format stays in web/.
 *
 * - Search folds accents and case and needs every term (AND) somewhere in the title,
 *   description, category, industry, collection and capability labels, or a team's member
 *   names.
 * - Within a dimension the values are OR'd; across dimensions they are AND'd. The libraries
 *   dimension is catalog.list's (H.25); the gallery does not set it.
 * - Original is a collection, not a category: an Original files under its topic and
 *   industries and carries the `original` keyword.
 * - Counts are recomputed against the other active filters, so a value that would empty
 *   the shelf reads 0, never missing.
 * - Recommended order: the shelf's ranked-first site's templates first, then the rest; in
 *   each, the newer templates before the Originals, each group by title.
 * - A shelf lists every library, or the libraries it names and the templates carrying the
 *   keywords it names; what it excludes is never listed, pinned or not.
 * - The shelf's pinned templates lead it and are counted under no filter; with `hideBlanks`
 *   every other blank is left off the shelf (its URL still opens).
 *
 * Rows are keyed on `templateId`. The rules take a `ShelfRules` object, and every shelf the
 * catalog snapshot serves (`CatalogShelf`, from `content/shelves/<id>.yaml`, V6 plan row H.24)
 * is one, so a front-end passes its shelf as it arrives. `catalog.list` takes its filters as
 * `CatalogFilters` and answers its counts as `CatalogListCounts`; `shelfFiltersOf` and
 * `catalogCountsOf` turn one into the other, so the server and a front-end agree (H.25).
 */
// ---------------------------------------------------------------------------
// The shelf file
/** The shelves' folder at the root of the content tree: one `<id>.yaml` per shelf. */
export const SHELVES_DIR = "shelves";
/** How a shelf names a template: `<library_key>/<slug>`, never a template id. */
export const SHELF_REF_PATTERN = /^[a-z0-9]+(?:[_-][a-z0-9]+)*\/[a-z0-9]+(?:[_-][a-z0-9]+)*$/;
const ShelfRefTextSchema = z
    .string()
    .regex(SHELF_REF_PATTERN, "a shelf names a template as <library_key>/<slug>, never by its id");
const ShelfLabelsSchema = z.strictObject({
    en: z.string().trim().min(1).max(80),
    es: z.string().trim().min(1).max(80),
});
/** A curated row: its labels and either the templates it names, in order, or a filter. */
export const ShelfCuratedRowSchema = z
    .strictObject({
    id: ContentKeySchema,
    labels: ShelfLabelsSchema,
    templates: z.array(ShelfRefTextSchema).min(1).max(24).optional(),
    filter: z
        .strictObject({
        category: ContentKeySchema.optional(),
        industries: z.array(ContentKeySchema).max(40).default([]),
        keywords: z.array(ContentKeySchema).max(40).default([]),
        capabilities: z.array(ContentKeySchema).max(20).default([]),
    })
        .optional(),
})
    .superRefine((row, context) => {
    if ((row.templates === undefined) === (row.filter === undefined)) {
        context.addIssue({
            code: "custom",
            path: [],
            message: "a row names its templates or a filter, exactly one of them",
        });
    }
    const filter = row.filter;
    if (filter &&
        !filter.category &&
        !filter.industries.length &&
        !filter.keywords.length &&
        !filter.capabilities.length) {
        context.addIssue({
            code: "custom",
            path: ["filter"],
            message: "a row's filter names a category, an industry, a keyword or a capability",
        });
    }
});
/**
 * `content/shelves/<id>.yaml` (V6 plan §13.3.3 "A shelf", D51): what a front-end lists, pins,
 * ranks first and features. The first-party shelves' ids are the site ids. A shelf names only
 * library keys, keywords and `<library_key>/<slug>` refs, so it survives republishing; the
 * content gate checks that each names a library or a template the tree holds, and each
 * keyword or term the vocabulary (content/taxonomy.yaml) holds.
 */
export const ShelfSchema = z
    .strictObject({
    apiVersion: z.literal(CONTENT_API_VERSION),
    kind: z.literal("Shelf"),
    metadata: z.strictObject({ id: ContentKeySchema }),
    lists: z.strictObject({
        libraries: z.union([z.literal("all"), z.array(ContentKeySchema).max(100)]),
        keywords: z.array(ContentKeySchema).max(40).default([]),
    }),
    excludes: z
        .strictObject({
        libraries: z.array(ContentKeySchema).max(100).default([]),
        templates: z.array(ShelfRefTextSchema).max(200).default([]),
    })
        .default({ libraries: [], templates: [] }),
    pinned: z.array(ShelfRefTextSchema).max(12).default([]),
    hideBlanks: z.boolean().default(false),
    rankFirst: z.strictObject({ site: SiteIdSchema }).optional(),
    rows: z.array(ShelfCuratedRowSchema).max(20).default([]),
})
    .superRefine((shelf, context) => {
    if (Array.isArray(shelf.lists.libraries) && !shelf.lists.libraries.length) {
        if (!shelf.lists.keywords.length) {
            context.addIssue({
                code: "custom",
                path: ["lists"],
                message: "a shelf lists `libraries: all`, some library keys or some keywords",
            });
        }
    }
    const duplicate = (path, values, what) => {
        const seen = new Set();
        values.forEach((value, index) => {
            if (seen.has(value))
                context.addIssue({
                    code: "custom",
                    path: [...path, index],
                    message: `duplicate ${what} "${value}"`,
                });
            seen.add(value);
        });
    };
    duplicate(["pinned"], shelf.pinned, "pinned template");
    duplicate(["rows"], shelf.rows.map((row) => row.id), "row id");
    const excludedLibraries = new Set(shelf.excludes.libraries);
    const excludedTemplates = new Set(shelf.excludes.templates);
    shelf.pinned.forEach((ref, index) => {
        if (excludedTemplates.has(ref) || excludedLibraries.has(ref.split("/")[0] ?? "")) {
            context.addIssue({
                code: "custom",
                path: ["pinned", index],
                message: `"${ref}" is pinned and excluded: a shelf does one or the other`,
            });
        }
    });
});
/** A `<library_key>/<slug>` ref as the pair a catalog row carries. */
export function shelfRefOf(text) {
    const at = text.indexOf("/");
    return { libraryKey: text.slice(0, at), slug: text.slice(at + 1) };
}
/**
 * Whether a row runs where the filter asks: a pair this deployment serves, on the provider (and
 * the runner, when named), whose verdict is ok, or degraded when `atLeast` allows it. A pair
 * nothing checked (verdict null) or that is blocked never passes.
 */
export function runsWhere(row, where) {
    const accepted = where.atLeast === "ok" ? ["ok"] : ["ok", "degraded"];
    return (row.runsOn ?? []).some((pair) => pair.served &&
        pair.provider === where.provider &&
        (where.runner === undefined || pair.runner === where.runner) &&
        pair.verdict !== null &&
        accepted.includes(pair.verdict));
}
// ---------------------------------------------------------------------------
// Rows
const kindOf = (row) => (row.kind === "team" ? "team" : "single");
const isInTeam = (row) => (row.memberOf?.length ?? 0) > 0;
const collectionsOf = (row) => row.tagKeys ?? [];
const capabilitiesOf = (row) => row.capabilityKeys ?? [];
/**
 * An Original carries the `original` keyword: the one test the card, the detail and the
 * order (newer templates before the Originals) share.
 */
export const isOriginal = (row) => (row.tagKeys ?? []).includes(ORIGINAL_KEYWORD);
/** A blank template: the slug `blank`, or one ending `-blank`. */
export const isBlankSlug = (slug) => slug === "blank" || Boolean(slug?.endsWith("-blank"));
const isRef = (row, ref) => row.libraryKey === ref.libraryKey && row.slug === ref.slug;
/** Whether a shelf keeps a row off: its library or the row itself is excluded. */
function isExcluded(row, rules) {
    const excludes = rules.excludes;
    if (!excludes)
        return false;
    return ((excludes.libraries ?? []).includes(row.libraryKey) ||
        (excludes.templates ?? []).some((ref) => isRef(row, ref)));
}
/** Whether a shelf lists a row: every library, its library, or one of its keywords. */
function isListed(row, rules) {
    const lists = rules.lists;
    if (!lists || lists.libraries === "all")
        return true;
    if (lists.libraries.includes(row.libraryKey))
        return true;
    return (lists.keywords ?? []).some((key) => collectionsOf(row).includes(key));
}
/**
 * Splits the rows into the shelf's pinned rows (in `rules.pinned` order, at most one row per
 * ref) and the shelf the filters work over. An excluded row is in neither. A row a pinned ref
 * names leads whether or not the shelf lists its library, and is never on the shelf; a row
 * the shelf does not list is left out, and with `hideBlanks` so is every other blank.
 */
export function splitShelf(rows, rules) {
    const pinnedByRef = rules.pinned.map(() => undefined);
    const shelf = [];
    for (const row of rows) {
        if (isExcluded(row, rules))
            continue;
        const at = rules.pinned.findIndex((ref) => isRef(row, ref));
        if (at >= 0) {
            pinnedByRef[at] ??= row;
            continue;
        }
        if (!isListed(row, rules))
            continue;
        if (rules.hideBlanks && isBlankSlug(row.slug))
            continue;
        shelf.push(row);
    }
    return { pinned: pinnedByRef.filter((row) => row !== undefined), shelf };
}
// ---------------------------------------------------------------------------
// Search
/** Lower case with the accents taken off (NFD, combining marks dropped): "Química" → "quimica". */
export function foldText(text) {
    return text
        .normalize("NFD")
        .replace(/\p{M}+/gu, "")
        .toLowerCase();
}
/** The folded terms of a search, each of which a row must contain. */
export function searchTerms(query) {
    return foldText(query).split(/\s+/).filter(Boolean);
}
// One folded text per row and label set: counting asks for it once per row and control.
const searchTextCache = new WeakMap();
/**
 * What a search term is matched against, folded: the title, the description, the
 * category, industry, keyword and capability labels, and a team's member names.
 */
function searchText(row, labels) {
    let byRow = searchTextCache.get(labels);
    if (!byRow) {
        byRow = new WeakMap();
        searchTextCache.set(labels, byRow);
    }
    const cached = byRow.get(row);
    if (cached !== undefined)
        return cached;
    const label = (map, key) => map?.get(key) ?? key;
    const text = foldText([
        row.name,
        row.description,
        row.categoryKey ? label(labels.categoryLabels, row.categoryKey) : "",
        ...(row.industryKeys ?? []).map((key) => label(labels.industryLabels, key)),
        ...[...collectionsOf(row), ...capabilitiesOf(row)].map((key) => label(labels.keywordLabels, key)),
        ...(row.members ?? []).map((member) => member.name),
    ].join("\n"));
    byRow.set(row, text);
    return text;
}
function matchesExcept(row, filters, terms, labels, skip) {
    if (!skip.has("q") && terms.length > 0) {
        const text = searchText(row, labels);
        if (!terms.every((term) => text.includes(term)))
            return false;
    }
    if (!skip.has("from") && filters.from !== "all" && row.siteId !== filters.from)
        return false;
    if (!skip.has("kind") && filters.kind !== "all" && kindOf(row) !== filters.kind)
        return false;
    if (!skip.has("inTeam") && filters.kind === "single" && filters.inTeam !== "any") {
        if (isInTeam(row) !== (filters.inTeam === "yes"))
            return false;
    }
    if (!skip.has("category") && filters.category !== null && row.categoryKey !== filters.category)
        return false;
    if (!skip.has("industries") &&
        filters.industries.length > 0 &&
        !(row.industryKeys ?? []).some((key) => filters.industries.includes(key)))
        return false;
    if (!skip.has("collections") &&
        filters.collections.length > 0 &&
        !collectionsOf(row).some((key) => filters.collections.includes(key)))
        return false;
    if (!skip.has("capabilities") &&
        filters.capabilities.length > 0 &&
        !capabilitiesOf(row).some((key) => filters.capabilities.includes(key)))
        return false;
    if (!skip.has("libraries") &&
        filters.libraries?.length &&
        !filters.libraries.includes(row.libraryKey))
        return false;
    if (!skip.has("runsOn") && filters.runsOn && !runsWhere(row, filters.runsOn))
        return false;
    return true;
}
const NONE = new Set();
/** Whether a row passes every active filter. */
export function matchesFilters(row, filters, labels) {
    return matchesExcept(row, filters, searchTerms(filters.q), labels, NONE);
}
/** The rows that pass every filter, in their given order. */
export function filterShelf(rows, filters, labels) {
    const terms = searchTerms(filters.q);
    return rows.filter((row) => matchesExcept(row, filters, terms, labels, NONE));
}
function bump(map, key) {
    map.set(key, (map.get(key) ?? 0) + 1);
}
/** Each control's counts against every OTHER active filter. */
export function countFacets(shelf, filters, labels) {
    const counts = {
        from: { all: 0, sites: new Map() },
        kind: { all: 0, team: 0, single: 0 },
        inTeam: { any: 0, yes: 0, no: 0 },
        categories: new Map(),
        industries: new Map(),
        collections: new Map(),
        capabilities: new Map(),
        libraries: new Map(),
    };
    const terms = searchTerms(filters.q);
    const skipFrom = new Set(["from"]);
    const skipKind = new Set(["kind", "inTeam"]);
    const skipInTeam = new Set(["inTeam"]);
    const skipCategory = new Set(["category"]);
    const skipIndustries = new Set(["industries"]);
    const skipCollections = new Set(["collections"]);
    const skipCapabilities = new Set(["capabilities"]);
    const skipLibraries = new Set(["libraries"]);
    const asSingle = { ...filters, kind: "single" };
    for (const row of shelf) {
        if (matchesExcept(row, filters, terms, labels, skipFrom)) {
            counts.from.all += 1;
            bump(counts.from.sites, row.siteId);
        }
        if (matchesExcept(row, filters, terms, labels, skipKind)) {
            counts.kind.all += 1;
            counts.kind[kindOf(row)] += 1;
        }
        if (matchesExcept(row, asSingle, terms, labels, skipInTeam)) {
            counts.inTeam.any += 1;
            counts.inTeam[isInTeam(row) ? "yes" : "no"] += 1;
        }
        if (row.categoryKey && matchesExcept(row, filters, terms, labels, skipCategory))
            bump(counts.categories, row.categoryKey);
        if (matchesExcept(row, filters, terms, labels, skipIndustries)) {
            for (const key of new Set(row.industryKeys ?? []))
                bump(counts.industries, key);
        }
        if (matchesExcept(row, filters, terms, labels, skipCollections)) {
            for (const key of new Set(collectionsOf(row)))
                bump(counts.collections, key);
        }
        if (matchesExcept(row, filters, terms, labels, skipCapabilities)) {
            for (const key of new Set(capabilitiesOf(row)))
                bump(counts.capabilities, key);
        }
        if (matchesExcept(row, filters, terms, labels, skipLibraries))
            bump(counts.libraries, row.libraryKey);
    }
    return counts;
}
// ---------------------------------------------------------------------------
// Order
const publishedTime = (row) => {
    const time = row.publishedAt ? Date.parse(row.publishedAt) : Number.NaN;
    return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};
/**
 * The shelf in the chosen order, ties broken by title in the viewer's locale, then by
 * template id. Recommended: the ranked-first site's templates first, then the rest (a shelf
 * that ranks no site first has one group); within each, the newer templates before the
 * Originals. Title: A–Z only. Newest: the latest to go
 * live first (a row that does not say sorts last).
 */
export function orderShelf(rows, locale, rules, sort = "recommended") {
    const collator = new Intl.Collator(locale, { sensitivity: "base", numeric: true });
    const byTitle = (a, b) => collator.compare(a.name, b.name) || a.templateId.localeCompare(b.templateId);
    const first = rules.rankFirst?.site;
    const elsewhere = (row) => (first === undefined ? 0 : Number(row.siteId !== first));
    const newer = (a, b) => {
        const [ta, tb] = [publishedTime(a), publishedTime(b)];
        return ta === tb ? 0 : ta < tb ? 1 : -1;
    };
    return [...rows].sort((a, b) => {
        if (sort === "title")
            return byTitle(a, b);
        if (sort === "newest")
            return newer(a, b) || byTitle(a, b);
        return (elsewhere(a) - elsewhere(b) || Number(isOriginal(a)) - Number(isOriginal(b)) || byTitle(a, b));
    });
}
// ---------------------------------------------------------------------------
// catalog.list (V6 plan §13.3.3 "The read API", `CatalogFilters`, row H.25)
/** catalog.list's orders: the shelf's Recommended, A–Z by title, or the latest to go live first. */
export const CatalogSortSchema = z.enum(["recommended", "title", "newest"]);
/**
 * What catalog.list narrows the shelf by: the gallery's filters, plus libraries. Within a
 * dimension the values are OR'd, across dimensions AND'd; an absent field or an empty list
 * narrows nothing. Keys are the shared vocabulary's (content/taxonomy.yaml) and the libraries'.
 */
export const CatalogFiltersSchema = z.object({
    q: z
        .string()
        .max(200)
        .optional()
        .describe("A search: every term, accents and case folded, somewhere in the title, the description, the category, industry, keyword or capability labels in the request locale, or a team's member names."),
    from: SiteIdSchema.optional().describe("Only the templates whose home site this is."),
    kind: CatalogTemplateKindSchema.optional().describe("Only teams, or only single agents."),
    inTeam: z
        .boolean()
        .optional()
        .describe("Only single agents: true those a live team names as a member, false those none does. It implies kind single; with kind team it is 400 invalid_filters."),
    category: ContentKeySchema.optional().describe("Only the templates filed under this category."),
    industries: z
        .array(ContentKeySchema)
        .max(40)
        .optional()
        .describe("Only the templates that serve one of these industries."),
    keywords: z
        .array(ContentKeySchema)
        .max(40)
        .optional()
        .describe("Only the templates carrying one of these keywords (tagKeys), original among them."),
    capabilities: z
        .array(ContentKeySchema)
        .max(40)
        .optional()
        .describe("Only the templates that can do one of these (capabilityKeys)."),
    libraries: z
        .array(ContentKeySchema)
        .max(100)
        .optional()
        .describe("Only the templates in one of these libraries, by library key."),
    runsOn: z
        .object({
        provider: RuntimeProviderSchema,
        runner: z.enum(ENGINE_TARGETS).optional(),
        atLeast: z.enum(["ok", "degraded"]).optional(),
    })
        .optional()
        .describe("Only the templates this deployment serves on the provider (and the runner, when named) with a verdict of ok, or ok or degraded when atLeast is degraded (the default): the rows' runsOn (wire 5.22.0)."),
});
const CountSchema = z.number().int().nonnegative();
const CountsByKeySchema = z.record(z.string(), CountSchema);
/**
 * catalog.list's counts: for each control, how many rows of the shelf (its pinned rows left
 * out, as the gallery counts) pass every OTHER active filter. A key no such row carries is
 * absent: read it as 0. `inTeam` counts single agents only.
 */
export const CatalogListCountsSchema = z.object({
    from: z.object({ all: CountSchema, sites: CountsByKeySchema }),
    kind: z.object({ all: CountSchema, team: CountSchema, single: CountSchema }),
    inTeam: z.object({ any: CountSchema, yes: CountSchema, no: CountSchema }),
    categories: CountsByKeySchema,
    industries: CountsByKeySchema,
    keywords: CountsByKeySchema,
    capabilities: CountsByKeySchema,
    libraries: CountsByKeySchema,
});
/** catalog.list's filters as the shelf rules read them: `inTeam` implies a single agent. */
export function shelfFiltersOf(filters = {}) {
    return {
        q: filters.q ?? "",
        from: filters.from ?? "all",
        kind: filters.kind ?? (filters.inTeam === undefined ? "all" : "single"),
        inTeam: filters.inTeam === undefined ? "any" : filters.inTeam ? "yes" : "no",
        category: filters.category ?? null,
        industries: filters.industries ?? [],
        collections: filters.keywords ?? [],
        capabilities: filters.capabilities ?? [],
        libraries: filters.libraries ?? [],
        runsOn: filters.runsOn,
    };
}
/**
 * Whether any filter narrows the shelf: then its pinned rows no longer lead it, as in the
 * gallery. A blank search narrows nothing.
 */
export function isFiltering(filters = {}) {
    return ((filters.q ?? "").trim() !== "" ||
        filters.from !== undefined ||
        filters.kind !== undefined ||
        filters.inTeam !== undefined ||
        filters.category !== undefined ||
        Boolean(filters.industries?.length) ||
        Boolean(filters.keywords?.length) ||
        Boolean(filters.capabilities?.length) ||
        Boolean(filters.libraries?.length) ||
        filters.runsOn !== undefined);
}
/** The shelf rules' counts as catalog.list answers them: plain objects for the maps. */
export function catalogCountsOf(counts) {
    return {
        from: { all: counts.from.all, sites: Object.fromEntries(counts.from.sites) },
        kind: { ...counts.kind },
        inTeam: { ...counts.inTeam },
        categories: Object.fromEntries(counts.categories),
        industries: Object.fromEntries(counts.industries),
        keywords: Object.fromEntries(counts.collections),
        capabilities: Object.fromEntries(counts.capabilities),
        libraries: Object.fromEntries(counts.libraries),
    };
}
//# sourceMappingURL=catalog-shelf.js.map